import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IconStar,
  IconCloudDownload,
  IconShieldLock,
  IconX,
  IconTrash,
} from "@tabler/icons-react";

import { getMemberInfo } from "@/hooks/useMembers";
import { useDocument, buildYearFilter } from "@/hooks/useDocument";
import { PAGE_SIZE } from "@/utils/constants";
import { formatDocumentDateShort } from "@/utils/date-utils";

import MDocumentCard from "@/components/ui/atoms/MDocumentCard";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import MInfiniteScroll from "@/components/ui/atoms/MInfiniteScroll";
import MLoader from "@/components/ui/atoms/MLoader";

import styles from "./document.module.css";
import { triggerNativeOfflineDoc } from "@/utils/nativeBridge";

const FILTER_STORAGE_KEY = "documentFilters";

const DocumentSelection = () => {
  const navigate = useNavigate();

  /** ------------------------------------------------------------
   *  Member + Document hooks
   * -------------------------------------------------------------*/
  const memberInfo = getMemberInfo();
  const memberId = memberInfo?.id || "";
  // When viewing another family member's documents, hide their private docs.
  const hidePrivateDocs = !!memberInfo?.onBehalf;
  const {
    fetchDocumentsWithSettings,
    bulkDownloadZip,
    bulkUpdateFlag,
    bulkDelete,
    vectorSearchDocuments,
  } = useDocument(memberId);

  /** Match DocumentListing's chronological order (newest first). */
  const documentDateSort = [
    { key: "documentDate", direction: "desc" as const },
  ];

  /** Excludes Health Memory docs (category 7) and, when viewing another
   * member, any documents they have marked as private. */
  const isVisibleDoc = (d: any) =>
    d.category !== 7 && !(hidePrivateDocs && d.isPrivate);

  /** ------------------------------------------------------------
   *  Filters + Search State
   * -------------------------------------------------------------*/
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInitialized, setSearchInitialized] = useState(false);

  const categoryParam = searchParams.get("category");
  const yearParam = searchParams.get("year");

  const [selectedCategories, setSelectedCategories] = useState<number[]>(
    categoryParam
      ? categoryParam
          .split(",")
          .map(Number)
          .filter((v) => !isNaN(v))
      : []
  );
  const [selectedYears, setSelectedYears] = useState<number[]>(
    yearParam
      ? yearParam
          .split(",")
          .map(Number)
          .filter((v) => !isNaN(v))
      : []
  );

  /** ------------------------------------------------------------
   *  Documents state
   * -------------------------------------------------------------*/
  const [documents, setDocuments] = useState<any[]>([]);
  const [allDocuments, setAllDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  /** Pagination */
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  /** ------------------------------------------------------------
   *  Selection
   * -------------------------------------------------------------*/
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  /** ------------------------------------------------------------
   *  Restore filters from localStorage
   * -------------------------------------------------------------*/
  useEffect(() => {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);

      if (!searchParams.get("search") && parsed.search) {
        setSearchTerm(parsed.search);
        searchParams.set("search", parsed.search);
      }

      if (!searchParams.get("category") && parsed.categories?.length > 0) {
        setSelectedCategories(parsed.categories);
        searchParams.set("category", parsed.categories.join(","));
      }

      if (!searchParams.get("year") && parsed.years?.length > 0) {
        setSelectedYears(parsed.years);
        searchParams.set("year", parsed.years.join(","));
      }

      setSearchParams(searchParams, { replace: true });
    } catch {
      /* noop -- restoring filter state from URL is best-effort */
    }
  }, []);

  /** ------------------------------------------------------------
   *  Sync searchTerm from URL
   * -------------------------------------------------------------*/
  useEffect(() => {
    const s = searchParams.get("search") || "";
    setSearchTerm(s);
    setSearchInitialized(true);
  }, [searchParams]);

  /** ------------------------------------------------------------
   *  Save filters to storage
   * -------------------------------------------------------------*/
  useEffect(() => {
    if (!searchInitialized) return;

    const save = {
      search: searchTerm,
      categories: selectedCategories,
      years: selectedYears,
    };

    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(save));
  }, [searchTerm, selectedCategories, selectedYears, searchInitialized]);

  /** ------------------------------------------------------------
   *  Fetch on filter/search change
   * -------------------------------------------------------------*/
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!searchInitialized) return;

    let timer: NodeJS.Timeout | null = null;

    async function fetchPage1() {
      try {
        setLoading(true);
        const filters: any = {};

        if (selectedCategories.length === 1) {
          filters.category = {
            operation: "equal",
            value: selectedCategories[0],
            type: "NUMBER",
          };
        } else if (selectedCategories.length > 1) {
          filters.category = {
            operation: "in",
            value: selectedCategories,
            type: "NUMBER",
          };
        }

        const yearFilter = buildYearFilter(selectedYears);
        if (yearFilter) filters.documentDate = yearFilter;

        filters.memberId = {
          operation: "EQUAL",
          value: memberId,
          type: "STRING",
        };

        const res = await fetchDocumentsWithSettings({
          filters,
          sort: documentDateSort,
          skipMemories: true,
          page: 1,
          pageSize: PAGE_SIZE,
        });

        const newDocs = Array.isArray((res as any)?.data)
          ? (res as any).data
          : [];
        const filtered = newDocs.filter(isVisibleDoc);

        setDocuments(filtered);
        setAllDocuments(filtered);
        setPage(2);

        setHasMore(newDocs.length === PAGE_SIZE);
      } finally {
        setLoading(false);
      }
    }

    async function fetchFuzzy() {
      try {
        setLoading(true);
        const filters: any = {};

        if (selectedCategories.length > 0) {
          filters.category = {
            operation: "in",
            value: selectedCategories,
            type: "NUMBER",
          };
        }

        const yearFilter = buildYearFilter(selectedYears);
        if (yearFilter) filters.documentDate = yearFilter;

        const results = await vectorSearchDocuments(searchTerm, filters, {
          skipMemories: true,
        });
        const arr = Array.isArray(results) ? results : [];

        setDocuments(arr.filter(isVisibleDoc));
        setPage(2);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }

    if (searchTerm.length >= 3) {
      timer = setTimeout(fetchFuzzy, isFirstRender.current ? 0 : 600);
    } else if (searchTerm.length > 0) {
      setDocuments(allDocuments);
      setHasMore(false);
    } else {
      fetchPage1();
    }

    isFirstRender.current = false;

    return () => timer && clearTimeout(timer);
  }, [
    searchTerm,
    selectedCategories,
    selectedYears,
    memberId,
    searchInitialized,
  ]);

  /** ------------------------------------------------------------
   *  Helpers
   * -------------------------------------------------------------*/
  const getSelectedDocs = () =>
    documents.filter((d) => selectedIds.includes(d._id || d.id));

  const handleBulk = async (
    field: "availableOffline" | "isPrivate" | "isImportant"
  ) => {
    const selectedDocs = getSelectedDocs();
    if (selectedDocs.length === 0) return;
    await bulkUpdateFlag(selectedDocs, field, true, setDocuments);

    // Trigger native bridge for offline storage (same as DocumentDrawer)
    if (
      field === "availableOffline" &&
      typeof globalThis !== "undefined" &&
      (globalThis as any).ReactNativeWebView
    ) {
      selectedDocs.forEach((doc) => {
        triggerNativeOfflineDoc({
          fileKey: doc.media?.fileKey,
          displayName: doc.displayName || doc.media?.fileName,
          availableOffline: true,
        });
      });
    }

    // Refetch documents with fresh settings after update
    if (field === "isImportant" || field === "availableOffline") {
      const filters: any = {};
      if (selectedCategories.length === 1) {
        filters.category = {
          operation: "equal",
          value: selectedCategories[0],
          type: "NUMBER",
        };
      } else if (selectedCategories.length > 1) {
        filters.category = {
          operation: "in",
          value: selectedCategories,
          type: "NUMBER",
        };
      }
      const yearFilter = buildYearFilter(selectedYears);
      if (yearFilter) {
        filters.documentDate = yearFilter;
      }
      filters.memberId = {
        operation: "EQUAL",
        value: memberId,
        type: "STRING",
      };

      const docs = await fetchDocumentsWithSettings({
        filters,
        sort: documentDateSort,
        skipMemories: false,
        page: 1,
        pageSize: PAGE_SIZE,
      });
      const data = Array.isArray(docs?.data) ? docs.data : [];
      const filtered = data.filter(isVisibleDoc);
      setDocuments(filtered);
      setAllDocuments(filtered);
    }
  };

  const processedDocuments = documents.filter(isVisibleDoc);

  /** ------------------------------------------------------------
   *  Loading
   * -------------------------------------------------------------*/
  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <MLoader size={56} color="#4C189A" />
      </div>
    );
  }

  /** ------------------------------------------------------------
   *  RENDER
   * -------------------------------------------------------------*/
  return (
    <div style={{ padding: "0 24px" }}>
      {/* Sticky Header Container */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          background: "#FFF",
          marginLeft: "-24px",
          marginRight: "-24px",
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingBottom: "16px",
        }}
      >
        {/* Top Bar */}
        <div className={styles.selectBar}>
          <button className={styles.selectBarBack} onClick={() => navigate(-1)}>
            <IconX size={24} />
          </button>
          <div className="heading-h7-bold">Select Documents</div>
          <div style={{ width: 36 }} />
        </div>

        {/* Bulk Actions */}
        <div className={styles.selectCtasRow}>
          <div
            className={[
              styles.ctaItem,
              selectedIds.length ? "" : styles.ctaItemDisabled,
            ].join(" ")}
            onClick={() => selectedIds.length && handleBulk("isImportant")}
          >
            <IconStar size={28} />
            <div>Favorite</div>
          </div>

          <div
            className={[
              styles.ctaItem,
              selectedIds.length ? "" : styles.ctaItemDisabled,
            ].join(" ")}
            onClick={() => selectedIds.length && handleBulk("availableOffline")}
          >
            <IconCloudDownload size={28} />
            <div>Offline</div>
          </div>

          {!hidePrivateDocs && (
            <div
              className={[
                styles.ctaItem,
                selectedIds.length ? "" : styles.ctaItemDisabled,
              ].join(" ")}
              onClick={() => selectedIds.length && handleBulk("isPrivate")}
            >
              <IconShieldLock size={28} />
              <div>Private</div>
            </div>
          )}

          <div
            className={[
              styles.ctaItem,
              selectedIds.length ? "" : styles.ctaItemDisabled,
            ].join(" ")}
            onClick={() => selectedIds.length && setBulkDeleteDialogOpen(true)}
          >
            <IconTrash size={28} color="#B3261E" />
            <div style={{ color: "#B3261E" }}>Delete</div>
          </div>
        </div>
      </div>

      {/* Selected Count */}
      <div className={styles.selectedCountRow}>
        <span style={{ fontWeight: 600, color: "#606060" }}>
          {selectedIds.length} documents selected
        </span>

        <MButton
          variant="link"
          size="md"
          disabled={selectedIds.length === 0}
          onClick={() => setSelectedIds([])}
          style={{ float: "right", position: "relative", top: "-10px" }}
        >
          Clear
        </MButton>
      </div>

      {/* Infinite Scroll */}
      <MInfiniteScroll
        data={processedDocuments} // ✔ FIX: filtered list
        hasMore={hasMore}
        isLoading={isLoadingPage}
        gap={0}
        onLoadMore={async () => {
          setIsLoadingPage(true);

          const filters: any = {};

          if (selectedCategories.length === 1) {
            filters.category = {
              operation: "equal",
              value: selectedCategories[0],
              type: "NUMBER",
            };
          } else if (selectedCategories.length > 1) {
            filters.category = {
              operation: "in",
              value: selectedCategories,
              type: "NUMBER",
            };
          }

          const yearFilter = buildYearFilter(selectedYears);
          if (yearFilter) filters.documentDate = yearFilter;

          filters.memberId = {
            operation: "EQUAL",
            value: memberId,
            type: "STRING",
          };

          const res = await fetchDocumentsWithSettings({
            filters,
            sort: documentDateSort,
            skipMemories: true,
            page,
            pageSize: PAGE_SIZE,
          });

          const newDocs = Array.isArray((res as any)?.data)
            ? (res as any).data
            : [];

          // ✔ FIX: Determine hasMore BEFORE filtering
          const hasMoreFromServer = newDocs.length === PAGE_SIZE;

          const filtered = newDocs.filter(isVisibleDoc);

          setDocuments((prev) => [...prev, ...filtered]);
          setAllDocuments((prev) => [...prev, ...filtered]);

          setPage((prev) => prev + 1);
          setHasMore(hasMoreFromServer);
          setIsLoadingPage(false);
        }}
        renderItem={(doc, idx) => {
          const id = doc._id || doc.id || idx.toString();
          const checked = selectedIds.includes(id);

          return (
            <MDocumentCard
              key={id}
              cardType="document"
              category={doc.category}
              displayName={doc.displayName || `Document ${idx + 1}`}
              meta={
                doc.documentDate
                  ? formatDocumentDateShort(doc.documentDate)
                  : ""
              }
              showComment={doc.notes}
              isPrivate={doc.isPrivate}
              isImportant={doc.settings?.isImportant || false}
              availableOffline={doc.settings?.availableOffline || false}
              insights={doc.insights || false}
              selectMode
              selected={checked}
              documentId={id}
              showStatusIcons={true}
              onSelect={() => {
                setSelectedIds((prev) =>
                  checked ? prev.filter((v) => v !== id) : [...prev, id]
                );
              }}
              onOptionsClick={() => {}}
            />
          );
        }}
      />

      {/* Bulk Delete Dialog */}
      <MDialog
        isOpen={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        title={`Delete ${selectedIds.length} document${
          selectedIds.length > 1 ? "s" : ""
        }?`}
        description={`${selectedIds.length > 1 ? "These" : "This"} document${
          selectedIds.length > 1 ? "s" : ""
        } will be permanently deleted.`}
        primaryCtaText="Yes"
        onPrimaryCta={async () => {
          await bulkDelete(getSelectedDocs(), setDocuments);
          setSelectedIds([]);
          setBulkDeleteDialogOpen(false);
        }}
        secondaryCtaText="No"
        onSecondaryCta={() => setBulkDeleteDialogOpen(false)}
      />
    </div>
  );
};

export default DocumentSelection;
